import React, { useEffect, useState, useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Modal, Button, Select, Input, FormField, Checkbox } from '@/components/ui';
import type { SelectOption } from '@/types/common.types';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import type { PatientSummary } from '@/types/patient.types';
import * as plansSvc from '@/services/plans.service';
import type { Plan, CreatePlanPayload } from '@/services/plans.service';
import * as adminSvc from '@/services/admin.service';
import * as crmSvc from '@/services/crm.service';
import { listDoctors, formatDoctorName } from '@/services/doctors.service';
import { listCoordinators, formatCoordinatorName } from '@/services/coordinators.service';
import { listNurses, formatNurseName } from '@/services/nurses.service';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import { getErrorMessage } from '@/services/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PlanCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (plan: Plan) => void;
  defaultPatientId?: number;
  /** When provided, the modal is in edit mode and PATCHes this plan instead of creating a new one. */
  plan?: Plan;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TIMEFRAME_OPTIONS: SelectOption<string>[] = [
  { label: 'ASAP', value: 'ASAP' },
  { label: '1–3 months', value: '1-3 months' },
  { label: '3–6 months', value: '3-6 months' },
  { label: '6–12 months', value: '6-12 months' },
  { label: '12+ months', value: '12+ months' },
];

const INTEREST_LEVEL_OPTIONS: SelectOption<number>[] = [
  { label: '1 — Very Low', value: 1 },
  { label: '2 — Low', value: 2 },
  { label: '3 — Moderate', value: 3 },
  { label: '4 — High', value: 4 },
  { label: '5 — Very High', value: 5 },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function PlanCreateModal({ open, onClose, onCreated, defaultPatientId, plan }: PlanCreateModalProps) {
  const isEdit = Boolean(plan);

  // Form state
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | null>(null);
  const [planName, setPlanName] = useState('');
  const [stageID, setStageID] = useState<number | null>(null);
  const [doctorID, setDoctorID] = useState<number | null>(null);
  const [coordID, setCoordID] = useState<number | null>(null);
  const [nurseID, setNurseID] = useState<number | null>(null);
  const [facilityID, setFacilityID] = useState<number | null>(null);
  const [timeframe, setTimeframe] = useState<string | null>(null);
  const [interestLevel, setInterestLevel] = useState<number | null>(null);
  const [financing, setFinancing] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (plan) {
      setSelectedPatient(null);
      setPlanName(plan.name ?? '');
      setStageID(plan.stageId ?? null);
      setDoctorID(plan.doctorId ?? null);
      setCoordID(plan.coordId ?? null);
      setNurseID(plan.nurseId ?? null);
      setFacilityID(plan.facilityId ?? null);
      setTimeframe(plan.timeframe ?? null);
      setInterestLevel(plan.interestLevel ?? null);
      setFinancing(Boolean(plan.financing));
      setServerError(null);
    }
  }, [open, plan]);

  // ─── Admin data queries ────────────────────────────────────────────────

  const { data: stagesData } = useQuery({
    queryKey: ['crm-stages'],
    queryFn: () => crmSvc.getCRMStages(),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  // Staff come from the role tables, not `resources`: plans store doctorID /
  // coordID / nurseID, which are a different ID space from resources.resourceID.
  // Shares its query keys with PlansTab's edit modal so both stay in step.
  const { data: doctors = [] } = useQuery({
    queryKey: ['doctors', 'active'],
    queryFn: () => listDoctors({ active: true }),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const { data: coordinators = [] } = useQuery({
    queryKey: ['coordinators', 'active'],
    queryFn: () => listCoordinators({ active: true }),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const { data: nurses = [] } = useQuery({
    queryKey: ['nurses', 'active'],
    queryFn: () => listNurses({ active: true }),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  const { roles } = useRoleTitles();

  const { data: facilities } = useQuery({
    queryKey: ['admin-facilities'],
    queryFn: adminSvc.listFacilities,
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });

  // ─── Derived option lists ──────────────────────────────────────────────

  const stageOptions: SelectOption<number>[] = (stagesData?.stages ?? []).map((s) => ({
    label: s.stageName ?? 'Unknown',
    value: Number(s.accountCustomStageID),
  }));

  const doctorLabel = roles.find((r) => r.roleType === 'Doctor')?.roleTitle || 'Doctor';
  const coordLabel = roles.find((r) => r.roleType === 'Coordinator')?.roleTitle || 'Coordinator';
  const nurseLabel = roles.find((r) => r.roleType === 'Nurse')?.roleTitle || 'Nurse';

  const doctorOptions: SelectOption<number>[] = doctors
    .map((d) => ({ label: formatDoctorName(d) || `Doctor #${d.doctorID}`, value: d.doctorID }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const coordOptions: SelectOption<number>[] = coordinators
    .map((c) => ({ label: formatCoordinatorName(c) || `Coordinator #${c.coordID}`, value: c.coordID }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const nurseOptions: SelectOption<number>[] = nurses
    .map((n) => ({ label: formatNurseName(n) || `Nurse #${n.nurseID}`, value: n.nurseID }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const facilityOptions: SelectOption<number>[] = (facilities ?? []).filter((f) => f.active).map((f) => ({
    label: f.facilityName ?? 'Unknown',
    value: f.facilityId,
  }));

  // ─── Mutation ─────────────────────────────────────────────────────────

  const mutation = useMutation({
    mutationFn: (payload: CreatePlanPayload) =>
      plan
        ? plansSvc.updatePlan(plan.planId, payload)
        : plansSvc.createPlan(payload),
    onSuccess: (result) => {
      if (!isEdit) handleReset();
      onCreated(result);
    },
    onError: (err) => {
      setServerError(getErrorMessage(err));
    },
  });

  // ─── Handlers ─────────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    setSelectedPatient(null);
    setPlanName('');
    setStageID(null);
    setDoctorID(null);
    setCoordID(null);
    setNurseID(null);
    setFacilityID(null);
    setTimeframe(null);
    setInterestLevel(null);
    setFinancing(false);
    setServerError(null);
  }, []);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setServerError(null);

      const patientId = plan?.patientId ?? selectedPatient?.patientId ?? defaultPatientId;
      if (!patientId) return;

      mutation.mutate({
        patientId,
        name: planName.trim() || undefined,
        stageId: stageID ?? undefined,
        // 0 = "None". Send 0 (not undefined) so editing a plan can actually
        // clear these — the backend only updates keys present in the request.
        doctorId: doctorID ?? 0,
        coordId: coordID ?? 0,
        nurseId: nurseID ?? 0,
        facilityId: facilityID ?? 0,
        timeframe: timeframe ?? undefined,
        interestLevel: interestLevel ?? undefined,
        financing,
      });
    },
    [
      plan,
      selectedPatient,
      defaultPatientId,
      planName,
      stageID,
      doctorID,
      coordID,
      nurseID,
      facilityID,
      timeframe,
      interestLevel,
      financing,
      mutation,
    ],
  );

  const patientRequired = !isEdit && defaultPatientId == null;
  const canSubmit =
    (isEdit || selectedPatient != null || defaultPatientId != null) && !mutation.isPending;

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={isEdit ? 'Edit Plan' : 'New Plan'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={mutation.isPending}
          >
            {isEdit ? 'Save Changes' : 'Create Plan'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {serverError && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{serverError}</p>
        )}

        {/* Patient — only shown when no defaultPatientId */}
        {patientRequired && (
          <FormField label="Patient" required>
            <PatientSearchInput
              onSelect={setSelectedPatient}
              selectedPatient={selectedPatient}
              placeholder="Search patients…"
            />
          </FormField>
        )}

        {/* Plan name */}
        <FormField label="Plan Name">
          <Input
            value={planName}
            onChange={(e) => setPlanName(e.target.value)}
            placeholder="e.g. Rhinoplasty Consult"
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          {/* Stage */}
          <FormField label="Stage">
            <Select<number>
              placeholder="Select stage…"
              options={stageOptions}
              value={stageID}
              onChange={setStageID}
              clearable
              searchable
            />
          </FormField>

          {/* Doctor */}
          <FormField label={doctorLabel}>
            <Select<number>
              placeholder={`Select ${doctorLabel.toLowerCase()}…`}
              options={doctorOptions}
              value={doctorID}
              onChange={setDoctorID}
              clearable
              searchable
            />
          </FormField>

          {/* Coordinator */}
          <FormField label={coordLabel}>
            <Select<number>
              placeholder={`Select ${coordLabel.toLowerCase()}…`}
              options={coordOptions}
              value={coordID}
              onChange={setCoordID}
              clearable
              searchable
            />
          </FormField>

          {/* Nurse */}
          <FormField label={nurseLabel}>
            <Select<number>
              placeholder={`Select ${nurseLabel.toLowerCase()}…`}
              options={nurseOptions}
              value={nurseID}
              onChange={setNurseID}
              clearable
              searchable
            />
          </FormField>

          {/* Facility */}
          <FormField label="Facility">
            <Select<number>
              placeholder="Select facility…"
              options={facilityOptions}
              value={facilityID}
              onChange={setFacilityID}
              clearable
              searchable
            />
          </FormField>

          {/* Timeframe */}
          <FormField label="Timeframe">
            <Select<string>
              placeholder="Select timeframe…"
              options={TIMEFRAME_OPTIONS}
              value={timeframe}
              onChange={setTimeframe}
              clearable
            />
          </FormField>

          {/* Interest level */}
          <FormField label="Interest Level">
            <Select<number>
              placeholder="Select interest level…"
              options={INTEREST_LEVEL_OPTIONS}
              value={interestLevel}
              onChange={setInterestLevel}
              clearable
            />
          </FormField>
        </div>

        {/* Financing */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="plan-financing"
            checked={financing}
            onChange={(e) => setFinancing(e.target.checked)}
          />
          <label htmlFor="plan-financing" className="text-sm text-slate-700 cursor-pointer select-none">
            Patient interested in financing
          </label>
        </div>
      </form>
    </Modal>
  );
}
